import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, InputGroup, Badge, Modal, Table, Tab, Nav } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaEye, FaEyeSlash, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, 
  FaLock, FaCheck, FaTimesCircle, FaHistory, FaKey, FaEdit, FaUserTie, FaUsers, FaBuilding, FaFileAlt } from 'react-icons/fa';
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import api, { rmApi, dsoApi } from '../../services/api.js';
import { locationService } from '../../services/location.js'; // Import locationService
import './AddDSO.css'; // Reusing the same CSS

const EditRM = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // State to control read-only mode (default to true for read-only)
  const [readOnly, setReadOnly] = useState(true);
  
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    state: '',
    city: '',
    pincode: '',
    status: ''
  });
  
  // State for mapped DSOs
  const [mappedDSOs, setMappedDSOs] = useState([]);
  const [isLoadingDSOs, setIsLoadingDSOs] = useState(false);

  // State for dynamic dropdowns
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [isFetchingStates, setIsFetchingStates] = useState(false);
  const [isFetchingCities, setIsFetchingCities] = useState(false);
  const [isFetchingPincodes, setIsFetchingPincodes] = useState(false);
  
  const [validated, setValidated] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');


  // Fetch states on component mount
  useEffect(() => {
    const fetchInitialStates = async () => {
      setIsFetchingStates(true);
      try {
        // Assuming getAllStates returns { data: { states: [{id: 1, name: 'StateA', code: 'SA'}, ...] } }
        // Adjust based on your actual API response structure in location.js
        const response = await locationService.getAllStates({ limit: 100 }); // Fetch all states
        // Ensure the response structure matches expectations
        const stateOptions = response.data?.states?.map(s => ({ value: s.id, label: s.name })) || [];
        setStates(stateOptions);
      } catch (error) {
        console.error("Failed to fetch states:", error);
        setErrorMessage('Failed to load states.');
        setShowError(true);
      } finally {
        setIsFetchingStates(false);
      }
    };
    fetchInitialStates();
  }, []); // Empty dependency array ensures this runs only once on mount
  
  
  // Fetch RM data on component mount
  useEffect(() => {
    fetchRMData();
    fetchMappedDSOs();
  }, [id]);
  
  // Fetch RM data using rmApi service
  const fetchRMData = async () => {
    if (!id) {
      setErrorMessage('Missing relationship manager ID');
      setShowError(true);
      return;
    }
    
    try {
      const response = await rmApi.getRelationshipManagerById(id);
      
      // Process response data and set in form structure
      const rmData = {
        ...response.data,
        state_id: response.data.state?.id || null,
        city_id: response.data.city?.id || null,
        pincode_id: response.data.pincode?.id || null,
        status: response.data.is_active ? 'Active' : 'Inactive'
      };
      console.log('Received RM data:', response.data);
      console.log('Processed form data:', rmData);
      
      // First set form data with existing values
      setFormData(rmData);
      
      // Then load location dropdowns sequentially
      if (response.data.state?.id) {
        // First load states (already loaded on component mount)
        
        // Then load cities for the selected state
        setIsFetchingCities(true);
        try {
          const citiesResponse = await locationService.getCitiesByState(response.data.state.id, { limit: 100 });
          const cityOptions = citiesResponse.data?.cities?.map(c => ({ value: c.id, label: c.name })) || [];
          setCities(cityOptions);
          
          // Then load pincodes if city is available
          if (response.data.city?.id) {
            setIsFetchingPincodes(true);
            try {
              const pincodesResponse = await locationService.getPincodesByCity(response.data.city.id, { limit: 100 });
              const pincodeOptions = pincodesResponse.data?.pincodes?.map(p => ({ value: p.id, label: p.code })) || [];
              setPincodes(pincodeOptions);
            } catch (error) {
              console.error("Failed to fetch pincodes:", error);
            } finally {
              setIsFetchingPincodes(false);
            }
          }
        } catch (error) {
          console.error("Failed to fetch cities:", error);
        } finally {
          setIsFetchingCities(false);
        }
      }
    } catch (error) {
      console.error('Error fetching RM data:', error);
      setErrorMessage('Failed to load relationship manager data');
      setShowError(true);
    }
  };
  
  // Fetch mapped DSOs using API with rm_id filter
  const fetchMappedDSOs = () => {
    setIsLoadingDSOs(true);
    
    // Use the dso API to fetch DSOs filtered by the RM's id
    // First check if we have an RM profile ID from the form data
    if (!id) {
      setErrorMessage('Missing relationship manager ID');
      setShowError(true);
      setIsLoadingDSOs(false);
      return;
    }
    
    // Use the dsoApi.getDsoList method with rm_id filter
    dsoApi.getDsoList({
      limit: 100, // Get a larger limit to show all DSOs
      rm_id: id // Filter by the RM's ID
    })
      .then(response => {
        // Map the API response to the structure expected by the component
        const formattedDSOs = response.data.items.map(dso => ({
          id: dso.id,
          name: dso.name,
          email: dso.email,
          phone: dso.phone,
          type: dso.dso_type || 'N/A',
          // For business type and individual type, use the data from the API if available
          businessType: dso.business_type || 'N/A',
          individualType: dso.individual_type || 'N/A',
          // Check if the DSO has documents based on flags from the API
          hasDocuments: dso.has_pan || dso.has_aadhaar || dso.has_company_cert || false
        }));
        
        setMappedDSOs(formattedDSOs);
      })
      .catch(error => {
        console.error('Error fetching mapped DSOs:', error);
        setErrorMessage('Failed to load mapped DSOs');
        setShowError(true);
      })
      .finally(() => {
        setIsLoadingDSOs(false);
      });
  };

  // Handle State Change - Fetch Cities
  const handleStateChange = async (selectedOption) => {
    const stateId = selectedOption ? parseInt(selectedOption.value, 10) : null;
    console.log("State selected:", stateId, selectedOption);
    
    setFormData(prev => ({
      ...prev,
      state_id: stateId,
      city_id: null, // Reset city
      pincode_id: null // Reset pincode
    }));
    setCities([]); // Clear cities dropdown
    setPincodes([]); // Clear pincodes dropdown

    if (stateId) {
      setIsFetchingCities(true);
      try {
        const response = await locationService.getCitiesByState(stateId, { limit: 100 });
        const cityOptions = response.data?.cities?.map(c => ({ value: c.id, label: c.name })) || [];
        console.log(`Loaded ${cityOptions.length} cities for state ID ${stateId}`);
        setCities(cityOptions);
      } catch (error) {
        console.error("Failed to fetch cities:", error);
        setErrorMessage('Failed to load cities for the selected state.');
        setShowError(true);
      } finally {
        setIsFetchingCities(false);
      }
    }
  };

  // Handle City Change - Fetch Pincodes
  const handleCityChange = async (selectedOption) => {
    const cityId = selectedOption ? parseInt(selectedOption.value, 10) : null;
    console.log("City selected:", cityId, selectedOption);
    
    setFormData(prev => ({
      ...prev,
      city_id: cityId,
      pincode_id: null // Reset pincode
    }));
    setPincodes([]); // Clear pincodes dropdown

    if (cityId) {
      setIsFetchingPincodes(true);
      try {
        const response = await locationService.getPincodesByCity(cityId, { limit: 100 });
        const pincodeOptions = response.data?.pincodes?.map(p => ({ value: p.id, label: p.code })) || [];
        console.log(`Loaded ${pincodeOptions.length} pincodes for city ID ${cityId}`, pincodeOptions);
        setPincodes(pincodeOptions);
      } catch (error) {
        console.error("Failed to fetch pincodes:", error);
        setErrorMessage('Failed to load pincodes for the selected city.');
        setShowError(true);
      } finally {
        setIsFetchingPincodes(false);
      }
    }
  };

  // Handle Pincode Change
  const handlePincodeChange = (selectedOption) => {
      const pincodeValue = selectedOption ? parseInt(selectedOption.value, 10) : null;
      console.log("Pincode selected:", pincodeValue, selectedOption);
      setFormData(prev => ({
          ...prev,
          pincode_id: pincodeValue
      }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  
  // Check password strength
  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength('');
      return;
    }
    
    let strength = 0;
    
    // Length check
    if (password.length >= 5) strength += 1;
    
    // Contains uppercase letters
    if (password.match(/[A-Z]/)) strength += 1;
    
    // Contains lowercase letters
    if (password.match(/[a-z]/)) strength += 1;
    
    // Contains numbers
    if (password.match(/[0-9]/)) strength += 1;
    
    switch (strength) {
      case 0:
      case 1:
        setPasswordStrength('weak');
        break;
      case 2:
        setPasswordStrength('medium');
        break;
      case 3:
      case 4:
        setPasswordStrength('strong');
        break;
      default:
        setPasswordStrength('');
    }
  };
  
  // Get password strength color
  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'strong':
        return 'success';
      default:
        return 'secondary';
    }
  };

  // Handle reset password
  const handleResetPassword = () => {
    // In a real app, this would call an API to reset the password
    console.log(`Resetting password for RM ID: ${id} with new password: ${newPassword}`);
    
    // Show success message
    setShowResetPasswordModal(false);
    setShowSuccess(true);
    setNewPassword('');
    
    // Clear success message after delay
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // If in readOnly mode, don't submit
    if (readOnly) {
      console.log("Form is in read-only mode, not submitting");
      return;
    }
    
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    // Prepare data for API
    const rmData = {
      name: formData.name,
      email: formData.email,
      mobile: formData.mobile,
      state_id: parseInt(formData.state_id, 10) || null,
      city_id: parseInt(formData.city_id, 10) || null,
      pincode_id: parseInt(formData.pincode_id, 10) || null,
      is_active: formData.status === 'Active'
    };
    
    // Log the data being sent to API for debugging
    console.log('Form data:', formData);
    console.log('Sending update data to API:', rmData);
    
    // Show loading state
    setShowSuccess(false);
    setShowError(false);
    
    // Call API to update the relationship manager
    rmApi.updateRelationshipManager(id, rmData)
      .then(response => {
        console.log('RM updated successfully:', response.data);
        setShowSuccess(true);
        setReadOnly(true); // Return to read-only mode after successful update
        fetchRMData(); // Refresh data
        
        // Clear success message after a short delay
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      })
      .catch(error => {
        console.error('Error updating RM:', error);
        const errorMsg = error.response?.data?.detail || 
                         error.message || 
                         'Failed to update relationship manager. Please try again.';
        setErrorMessage(errorMsg);
        setShowError(true);
      });
  };
  
  return (
    <Container fluid className="p-3">
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <h4 className="mb-1">
            <FaArrowLeft 
              className="me-2 cursor-pointer" 
              onClick={() => navigate('/admin/rm-list')}
              style={{ cursor: 'pointer' }}
            />
            View/Edit Relationship Manager
          </h4>
          <p className="text-muted small mb-0">Manage relationship manager details</p>
        </Col>
      </Row>
      
      {/* Status Bar */}
      <Row className="mb-3">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Body className="py-2">
              <Row>
                <Col md={12}>
                  <div className="d-flex align-items-center">
                    <FaCheck className="text-muted me-2" />
                    <div className="text-muted small me-2">Status:</div>
                    <Badge 
                      bg={formData.is_active === true ? 'success' : 'warning'}
                    >
                      {formData.is_active === true ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} className="d-flex justify-content-end">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => setShowResetPasswordModal(true)}
          >
            <FaKey className="me-1" /> Reset Password
          </Button>
        </Col>
      </Row>
      
      {/* Success and Error Alerts */}
      {showSuccess && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
              RM updated successfully!
            </Alert>
          </Col>
        </Row>
      )}
      
      {showError && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setShowError(false)} dismissible>
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}
      
      <Tab.Container defaultActiveKey="details">
        <Row>
          <Col md={3} lg={2} className="mb-3">
            <Nav variant="pills" className="flex-column shadow-sm">
              <Nav.Item>
                <Nav.Link eventKey="details" className="d-flex align-items-center">
                  <FaUser className="me-2" /> Details
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="dsos" className="d-flex align-items-center">
                  <FaUsers className="me-2" /> Mapped DSOs
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
          
          <Col md={9} lg={10}>
            <Card className="shadow-sm">
              <Card.Body>
                <Tab.Content>
                  {/* Details Tab */}
                  <Tab.Pane eventKey="details">
                    <Form noValidate validated={validated} onSubmit={handleSubmit} className="compact-form">
                      {/* Two Column Layout for the Form */}
            <Row className="g-3">
              <Col md={6}>
                {/* Personal Information Section */}
                <h6 className="section-header text-primary border-bottom">
                  <FaUser className="me-2" /> Personal Information
                </h6>
                
                <Form.Group className="mb-3">
                  <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaUser /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Full Name"
                      required
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a name.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      required
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid email address.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaPhone /></InputGroup.Text>
                    <Form.Control
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      placeholder="Phone Number (10 digits)"
                      pattern="[0-9]{10}"
                      required
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid 10-digit phone number.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

              </Col>
              
              <Col md={6}>
                {/* Location Information Section */}
                <h6 className="section-header text-primary border-bottom">
                  <FaMapMarkerAlt className="me-2" /> Location
                </h6>
                
                        <Form.Group controlId="formState">
                          <Form.Label>State *</Form.Label>
                          <Select
                           name="state_id"
                           options={states}
                           value={states.find(option => parseInt(option.value) === parseInt(formData.state_id)) || null}
                           onChange={handleStateChange}
                           placeholder="Select State"
                           isLoading={isFetchingStates}
                           isDisabled={readOnly || isFetchingStates}
                           classNamePrefix="react-select"
                           isClearable={!readOnly}
                           required={!readOnly}
                          />
                          {validated && !formData.state_id && (
                    <div className="invalid-feedback" style={{display: 'block'}}>
                      Please select a state.
                    </div>
                  )}
                </Form.Group>
                
                <Row className="g-2">
                  <Col md={6}>
                        <Form.Group controlId="formCity">
                          <Form.Label>City *</Form.Label>
                          <Select
                           name="city_id"
                           options={cities}
                           value={cities.find(option => parseInt(option.value) === parseInt(formData.city_id)) || null}
                           onChange={handleCityChange}
                           placeholder="Select City"
                           isLoading={isFetchingCities}
                           isDisabled={readOnly || !formData.state_id || isFetchingCities}
                           classNamePrefix="react-select"
                           isClearable={!readOnly}
                           required={!readOnly}
                          />
                          {validated && !formData.city_id && (
                        <div className="invalid-feedback" style={{display: 'block'}}>
                          Please select a city.
                        </div>
                      )}
                          {formData.city_id && cities.length === 0 && !isFetchingCities && (
                        <div className="text-info small mt-1">
                          City will load after selecting a state
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                        <Form.Group controlId="formPincode">
                          <Form.Label>Pincode *</Form.Label>
                          <Select
                           name="pincode_id" 
                           options={pincodes}
                           value={pincodes.find(option => parseInt(option.value) === parseInt(formData.pincode_id)) || null}
                           onChange={handlePincodeChange}
                           placeholder="Select Pincode"
                           isLoading={isFetchingPincodes}
                           isDisabled={readOnly || !formData.city_id || isFetchingPincodes}
                           classNamePrefix="react-select"
                           isClearable={!readOnly}
                           required={!readOnly}
                          />
                          {validated && !formData.pincode_id && (
                        <div className="invalid-feedback" style={{display: 'block'}}>
                          Please select a pincode.
                        </div>
                      )}
                          {formData.pincode_id && pincodes.length === 0 && !isFetchingPincodes && (
                        <div className="text-info small mt-1">
                          Pincode will load after selecting a city
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {/* Status Section */}
                <h6 className="section-header text-primary border-bottom">
                  <FaCheck className="me-2" /> Status Information
                </h6>
                
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    disabled={readOnly}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <hr className="my-3" />
            
            <Row>
              <Col className="d-flex gap-2 action-buttons">
                {readOnly ? (
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={(e) => {
                      e.preventDefault(); // Prevent any form submission
                      setValidated(false); // Reset validation state
                      setReadOnly(false);  // Enable editing
                      setShowSuccess(false); // Hide any success message
                      console.log("Enabling edit mode");
                    }}
                  >
                    <FaEdit className="me-1" /> Edit
                  </Button>
                ) : (
                  <Button 
                    variant="primary" 
                    type="submit"
                    size="sm"
                  >
                    <FaSave className="me-1" /> Save Changes
                  </Button>
                )}
                
                <Button 
                  variant="outline-secondary" 
                  onClick={() => navigate('/admin/rm-list')}
                  size="sm"
                >
                  Back to List
                </Button>
                
                {!readOnly && (
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={(e) => {
                      e.preventDefault(); // Prevent any form submission
                      setReadOnly(true);  // Return to read-only
                      setValidated(false); // Reset validation state
                      setShowSuccess(false); // Hide any success message
                      console.log("Cancelling edit mode");
                      fetchRMData(); // Reload original data to discard changes
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
                  </Tab.Pane>
                  
                  {/* Mapped DSOs Tab */}
                  <Tab.Pane eventKey="dsos">
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <h6 className="section-header text-primary border-bottom mb-0">
                        <FaUsers className="me-2" /> Mapped DSO List
                      </h6>
                      <div>
                        <span className="fw-bold text-muted me-2">Total DSOs:</span>
                        <Badge bg="primary" pill>
                          {mappedDSOs.length}
                        </Badge>
                      </div>
                    </div>
                    
                    {isLoadingDSOs ? (
                      <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading mapped DSOs...</p>
                      </div>
                    ) : mappedDSOs.length > 0 ? (
                      <Table responsive striped hover className="mt-3">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>DSO Details</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mappedDSOs.map((dso, index) => (
                            <tr key={dso.id}>
                              <td>{index + 1}</td>
                              <td>
                                {dso.type === 'Business' ? (
                                  <div className="d-flex flex-column">
                                    <Badge bg="info" className="mb-1">
                                      <FaBuilding className="me-1" /> Business
                                    </Badge>
                                    
                                  </div>
                                ) : (
                                  <div className="d-flex flex-column">
                                    <Badge bg="success" className="mb-1">
                                      <FaUserTie className="me-1" /> Individual
                                    </Badge>
                                    
                                  </div>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <div className="fw-bold">{dso.name}</div>
                                  <div className="small">
                                    <FaEnvelope className="text-muted me-1" /> {dso.email}
                                  </div>
                                  <div className="small">
                                    <FaPhone className="text-muted me-1" /> {dso.phone}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => navigate(`/admin/dso/${dso.id}/edit`)}
                                >
                                  <FaUser className="me-1" /> View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info" className="mt-3">
                        No DSOs are currently mapped to this relationship manager.
                      </Alert>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Tab.Container>
      
      {/* Reset Password Modal */}
      <Modal show={showResetPasswordModal} onHide={() => setShowResetPasswordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Generate a new password for this relationship manager.</p>
          <p className="mb-0 text-muted">RM Name: {formData.name}</p>
          
          <Form.Group className="mt-3">
            <Form.Label>New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  checkPasswordStrength(e.target.value);
                }}
                placeholder="Enter new password"
              />
              <Button 
                variant="outline-secondary"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </Button>
            </InputGroup>
            
            {newPassword && (
              <Form.Text>
                Password strength: {passwordStrength && (
                  <span className={`text-${getPasswordStrengthColor()} fw-bold`}>
                    {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                  </span>
                )}
              </Form.Text>
            )}
          </Form.Group>
          
          <div className="password-checklist mt-2">
            <div className={`d-flex align-items-center ${newPassword && newPassword.length >= 5 ? 'text-success' : 'text-muted'}`}>
              <span className="me-1">✓</span> At least 5 characters
            </div>
            <div className={`d-flex align-items-center ${newPassword && newPassword.match(/[A-Z]/) ? 'text-success' : 'text-muted'}`}>
              <span className="me-1">✓</span> At least one uppercase letter
            </div>
            <div className={`d-flex align-items-center ${newPassword && newPassword.match(/[a-z]/) ? 'text-success' : 'text-muted'}`}>
              <span className="me-1">✓</span> At least one lowercase letter
            </div>
            <div className={`d-flex align-items-center ${newPassword && newPassword.match(/[0-9]/) ? 'text-success' : 'text-muted'}`}>
              <span className="me-1">✓</span> At least one number
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetPasswordModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleResetPassword}
            disabled={!newPassword || passwordStrength !== 'strong'}
          >
            <FaKey className="me-1" /> Reset Password
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EditRM;
